//* Importing Libries and modules
import React, { useState, useCallback, useEffect } from "react";

//* Importing react-router hooks
import { useNavigate } from "react-router-dom";

//* Importing components
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//* Importing custom hooks
import { useTeams } from "../../../hooks/teams/useTeams";

//* Importing types from types
import { Team } from "../../../hooks/teams/types/types";

//* Import the AuthAPI service
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Import the UserRole enum
import { UserRole } from "../../../constants/enum/UserRole";

type User = {
  role: UserRole;
};

var debounce = require("lodash.debounce");

export function ClassView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [me, setMe] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoadingRefetch, setIsLoadingRefetch] = useState<boolean>(false);
  const [totalTeams, setTotalTeams] = useState<number>(0);

  const {
    teams,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteTeam,
    refetch,
    changePage,
    isDeleting,
  } = useTeams(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  useEffect(() => {
    setTotalTeams(pagination.totalItems || teams.length);
  }, [pagination.totalItems, teams.length]);

  const debouncedSearch = debounce((value: string) => {
    if (value.trim().length >= 3) {
      setSearch(value);
    } else {
      setSearch("");
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }
    setIsSearching(false);
    setIsLoadingRefetch(true); // Ativa o loading para o refetch
    refetch().finally(() => setIsLoadingRefetch(false)); // Finaliza o loading ao terminar o refetch
  }, 500);

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (teamToDelete) {
      try {
        const currentPageItems = teams.length;
        const currentPage = pagination.currentPage;

        setIsLoadingRefetch(true); 
        await deleteTeam(teamToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        } else {
        }

        setShowWarning(false);
        setTeamToDelete(null);
      } catch (error) {
        console.error("Error deleting team:", error);
      } finally {
        setIsLoadingRefetch(false);
      }
    }
  }, [
    teamToDelete,
    deleteTeam,
    teams.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setTeamToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    async (value: number) => {
      setIsLoadingRefetch(true);
      changePage(value);
      await refetch(); 
      setIsLoadingRefetch(false);
    },
    [changePage, refetch]
  );

  if (!me || isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        search_text="Pesquise pelo nome da turma"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={() => {
          setIsLoadingRefetch(true);
          refetch().finally(() => setIsLoadingRefetch(false));
        }}
        title={`Turmas (Total: ${totalTeams})`}
        not_found_message="Nenhum turma encontrado"
        button_link="/turmas/create"
        button_text="Adicionar turma"
        deleting_message="Ao deletar esta turma, ela não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onManagerActionClick={(id) => navigate(`/turmas/view/${id}`)}
        onEditActionClick={(id) => navigate(`/turmas/edit/${id}`)}
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching || isLoadingRefetch} 
        items={teams.map((team: Team) => ({
          ...team,
          key: team.id,
          isFirstSemester: team.is_first_semester ? "1" : "2",
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "course.name", type: "text", header: "Curso" },
          { name: "isFirstSemester", type: "text", header: "Período" },
          { name: "unit.name", type: "text", header: "Unidade" },
        ]}
        loading_search={isSearching}
      />
    </div>
  );
}
